import { ValidationWarning } from './types';

/**
 * Compile a pattern into an anchored regex. `[*]` becomes `\[\d+\]`; all
 * other regex metacharacters are escaped. The regex matches an exact path
 * (e.g. `folders[*].folder_id` matches `folders[5].folder_id` but not
 * `folders[5]` or `folders[5].folder_id.extra`).
 */
const patternToRegex = (pattern: string): RegExp => {
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const withWildcards = escaped.split('\\[\\*\\]').join('\\[\\d+\\]');
  return new RegExp(`^${withWildcards}$`);
};

/**
 * Extract the deepest enclosing array's path + index from a warning path.
 * For `folders[5].folder_id` returns `{ arrayPath: 'folders', index: 5 }`.
 * For `a[2].b[3].c` returns `{ arrayPath: 'a[2].b', index: 3 }`.
 * Returns `null` if the path contains no `[N]` segment.
 */
const extractLastIndex = (warningPath: string): { arrayPath: string; index: number } | null => {
  const m = warningPath.match(/^(.*)\[(\d+)\][^[]*$/);
  if (m === null) return null;
  return { arrayPath: m[1], index: parseInt(m[2], 10) };
};

/** Tokenize a dot/bracket path into navigation segments. `''` → `[]`. */
const segments = (path: string): (string | number)[] => {
  if (path === '') return [];
  const out: (string | number)[] = [];
  const re = /([^.[\]]+)|\[(\d+)\]/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(path)) !== null) {
    if (m[1] !== undefined) out.push(m[1]);
    else if (m[2] !== undefined) out.push(parseInt(m[2], 10));
  }
  return out;
};

/**
 * Walk `segs` into `value` and replace the targeted array via `update`.
 * Rebuilds intermediate objects/arrays immutably so deeply-readonly inputs
 * are not mutated. If a segment doesn't resolve (missing field, non-array
 * where an index was expected), returns the original value unchanged.
 */
const updateAt = (value: unknown, segs: readonly (string | number)[], update: (atArray: readonly unknown[]) => readonly unknown[]): unknown => {
  if (segs.length === 0) {
    if (!Array.isArray(value)) return value;
    return update(value);
  }
  const [head, ...rest] = segs;
  if (typeof head === 'number') {
    if (!Array.isArray(value)) return value;
    const copy = value.slice();
    copy[head] = updateAt(value[head], rest, update);
    return copy;
  }
  if (typeof value !== 'object' || value === null) return value;
  const obj = value as Record<string, unknown>;
  return { ...obj, [head]: updateAt(obj[head], rest, update) };
};

/**
 * Apply thunk-level policy to a validated value based on its warnings.
 *
 * Patterns are path globs. A pattern containing `[*]` is a *drop-row*
 * directive: for each warning whose path matches, the row identified by
 * the deepest `[N]` in that warning's path is removed from its enclosing
 * array. A pattern without `[*]` is a *fail-thunk* directive: if any
 * matching warning exists, this throws — the response is unusable.
 *
 * Drops on the same array are batched into a single filter pass. The
 * input value is treated as immutable; intermediates are rebuilt so
 * deeply-readonly types stay valid.
 *
 * @example
 * ```ts
 * const r = runSchema(FolderListSchema, raw, 'getFolders');
 * if (!r.ok) throw new Error('getFolders: structural validation failed');
 * return enforce(r.value, r.warnings, [
 *   'folders[*].folder_id',        // drop row if folder_id drifted
 *   'folders[*].parent_folder_id', // drop row if parent ref drifted
 *   'pagination.total_records',    // fail thunk
 *   'pagination.total_pages',      // fail thunk
 * ]);
 * ```
 */
export const enforce = <T>(value: T, warnings: ReadonlyMap<string, ValidationWarning>, patterns: readonly string[]): T => {
  const dropPatterns: RegExp[] = [];
  const failPatterns: RegExp[] = [];
  for (const p of patterns) {
    const re = patternToRegex(p);
    if (p.includes('[*]')) dropPatterns.push(re);
    else failPatterns.push(re);
  }

  const criticalDrifts: string[] = [];
  const dropsByArrayPath = new Map<string, Set<number>>();

  for (const w of warnings.values()) {
    for (const re of failPatterns) {
      if (re.test(w.path)) {
        criticalDrifts.push(w.path);
        break;
      }
    }
    for (const re of dropPatterns) {
      if (re.test(w.path)) {
        const ex = extractLastIndex(w.path);
        if (ex === null) break;
        let set = dropsByArrayPath.get(ex.arrayPath);
        if (set === undefined) {
          set = new Set<number>();
          dropsByArrayPath.set(ex.arrayPath, set);
        }
        set.add(ex.index);
        break;
      }
    }
  }

  if (criticalDrifts.length > 0) {
    throw new Error(`enforce: critical field(s) drifted: ${criticalDrifts.join(', ')}`);
  }

  let result: unknown = value;
  for (const [arrayPath, indices] of dropsByArrayPath) {
    const segs = segments(arrayPath);
    result = updateAt(result, segs, (arr) => arr.filter((_, i) => !indices.has(i)));
  }
  return result as T;
};
